import styled from '@emotion/styled';
import React, {useCallback, useRef, useState} from 'react';

import {Delete, ExclamationMark, Text} from '@ELib/assets/images';
import {ERegularText} from '@ELib/components/EText';
import {EDynamicTooltip} from '@ELib/components/ETooltip';
import {useDrop} from '@ELib/functions/drop';
import {mixinOverflow, mixinSVGIcon, mixinTruncate} from '@ELib/styles/mixins';

import {
  classy,
  formatBytes,
  isUploadFileSizeExceeded,
  useMountEffect,
} from '../../utils';
import {DropAreaContent} from '../DropArea';
import {EIconButton, EUndecoratedButton} from '../EButtons';

import {FormInputWrapper} from './FormInputWrapper';
import {SharedProps} from './sharedProps';

export type EFileUploadProps = SharedProps & {
  onChange: (files: File[] | File | undefined) => void;
  acceptedMimetypes?: string[];
  textDropArea: string;
  textUploadButton: string;
  multiple?: boolean;
  uploadList?: boolean;
  value?: File[];
  maxSizeInBytes?: number;
};

export const EFileUpload = ({
  onChange,
  acceptedMimetypes,
  textDropArea,
  textUploadButton,
  disabled = false,
  label,
  labelInfo,
  ariaLabel,
  bottomText,
  name,
  error,
  errorMessage,
  multiple = false,
  uploadList = false,
  value,
  maxSizeInBytes,
}: EFileUploadProps): React.ReactElement => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [filesToUpload, setFilesToUpload] = useState<Set<File>>(new Set());

  const handleDropAreaClick = useCallback(() => {
    fileRef.current?.click();
  }, [fileRef]);

  const addFiles = useCallback(
    (files: File[]) => {
      if (!disabled) {
        const updatedFilesToUpload = multiple
          ? new Set([...filesToUpload, ...files])
          : new Set(files);
        setFilesToUpload(updatedFilesToUpload);
        onChange(multiple ? [...updatedFilesToUpload] : files[0]);
      }
    },
    [disabled, filesToUpload, multiple, onChange]
  );

  const removeFile = useCallback(
    (fileToDelete: File) => {
      filesToUpload.delete(fileToDelete);
      setFilesToUpload(new Set([...filesToUpload]));
      onChange(filesToUpload.size === 0 ? undefined : [...filesToUpload]);
    },
    [filesToUpload, onChange]
  );

  useMountEffect(() => {
    if (value) {
      addFiles(value);
    }
  });

  const {dropArea} = useDrop({
    content: (
      <>
        <StyledFileUploadInput
          ref={fileRef}
          accept={acceptedMimetypes?.join(',')}
          aria-label={ariaLabel}
          data-testid='fileInput'
          multiple={multiple}
          type='file'
          onChange={({target}) => {
            if (target.files && target.files.length) {
              addFiles([...target.files]);
            }
          }}
        />
        <DropAreaContent>
          <span>{textDropArea}</span>
          <EUndecoratedButton disabled={disabled} inlineText={true}>
            {textUploadButton}
          </EUndecoratedButton>
        </DropAreaContent>
      </>
    ),
    fileKeys: acceptedMimetypes,
    onlyAcceptingFiles: true,
    inactive: disabled,
    onClick: handleDropAreaClick,
    onDropAccept: (_, files) => addFiles([...files]),
  });

  return (
    <FormInputWrapper
      bottomText={bottomText}
      error={error}
      errorMessage={errorMessage}
      label={label}
      labelInfo={labelInfo}
      labelProps={{htmlFor: name}}
    >
      {uploadList && !multiple && filesToUpload.size === 1 ? null : dropArea}
      {uploadList && filesToUpload.size > 0 ? (
        <FileUploadList
          className={classy({
            marginToDropArea: multiple,
          })}
        >
          {[...filesToUpload].map((file) => (
            <FileUploadListItem key={file.name}>
              <FileUploadItemInfo>
                {maxSizeInBytes !== undefined &&
                isUploadFileSizeExceeded(file, maxSizeInBytes) ? (
                  <ExclamationMark className='error' />
                ) : (
                  <Text />
                )}
                <EDynamicTooltip title={file.name}>
                  <span>{file.name}</span>
                </EDynamicTooltip>
              </FileUploadItemInfo>
              <ERegularText>{formatBytes(file.size)}</ERegularText>
              <EIconButton onClick={() => removeFile(file)}>
                <Delete />
              </EIconButton>
            </FileUploadListItem>
          ))}
        </FileUploadList>
      ) : null}
    </FormInputWrapper>
  );
};

const StyledFileUploadInput = styled.input`
  display: none;
`;

const FileUploadList = styled.ul`
  max-height: var(--file-list-max-height);
  ${mixinOverflow};

  border-bottom: var(--regular-border-width) solid
    var(--upload-added-list-border-color);
  margin: 0;
  padding-left: 0;
  padding-bottom: var(--gutter-little);

  &.marginToDropArea {
    margin-top: var(--gutter);
  }
`;

const FileUploadListItem = styled.li`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: var(--gutter-tiny);
`;

const FileUploadItemInfo = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;

  svg {
    ${mixinSVGIcon};
    margin-right: var(--gutter-tiny);

    &.error {
      color: var(--color-validation-error);
    }
  }

  span {
    ${mixinTruncate}
  }
`;
